/**
 * Schedule Service (Web)
 *
 * Owns shift schedule CRUD and employee schedule assignment at
 * /companies/{companyId}/schedules/{scheduleId}.
 *
 * Extracted from src/firebase.js per the service-decomposition plan
 * (docs/architecture/scalability-architecture.md §4). firebase.js retains
 * temporary re-export shims marked `// TODO remove shim — refactor:scheduleService`.
 *
 * Public API:
 *   createSchedule(scheduleData, companyId)
 *   getSchedules(companyId)
 *   updateSchedule(scheduleId, updates, companyId)
 *   deleteSchedule(scheduleId, companyId)
 *   assignEmployeesToSchedule(scheduleId, employeeIds, companyId)
 *
 * Cross-cutting rules:
 *   - Tenant scoping enforced by Firebase rules; this service trusts auth context.
 *   - deleteSchedule is a soft delete (marks isActive: false, never removes the node).
 *   - getSchedules returns only active schedules (isActive !== false).
 */

import { ref, set, get, push, update } from 'firebase/database';
import { getFirebase } from '../firebase';

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

// ─── Schedules ────────────────────────────────────────────────────────────────

/**
 * Create a new schedule entry for a company.
 */
export const createSchedule = async (scheduleData, companyId) => {
  try {
    const { database } = getFirebase();

    const schedulesPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/schedules`
      : 'schedules';

    const schedulesRef = ref(database, schedulesPath);
    const newScheduleRef = push(schedulesRef);

    const scheduleToStore = {
      id: newScheduleRef.key,
      name: scheduleData.name,
      description: scheduleData.description || null,
      shifts: scheduleData.shifts || [],
      assignedEmployees: scheduleData.assignedEmployees || [],
      locationId: scheduleData.locationId || null,
      isActive: scheduleData.isActive !== false,
      companyId: companyId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: scheduleData.createdBy || null
    };

    await set(newScheduleRef, scheduleToStore);
    dbg('Schedule created:', scheduleToStore.name);
    return scheduleToStore;
  } catch (error) {
    console.error('Error creating schedule:', error);
    throw error;
  }
};

/**
 * Fetch all active schedules for a company.
 */
export const getSchedules = async (companyId) => {
  try {
    const { database } = getFirebase();

    const schedulesPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/schedules`
      : 'schedules';

    const schedulesRef = ref(database, schedulesPath);
    const snapshot = await get(schedulesRef);

    if (snapshot.exists()) {
      const schedules = snapshot.val();
      return Object.values(schedules).filter(sched => sched.isActive !== false);
    }
    return [];
  } catch (error) {
    console.error('Error getting schedules:', error);
    throw error;
  }
};

/**
 * Apply partial updates to an existing schedule.
 */
export const updateSchedule = async (scheduleId, updates, companyId) => {
  try {
    const { database } = getFirebase();

    const schedulePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/schedules/${scheduleId}`
      : `schedules/${scheduleId}`;

    const scheduleRef = ref(database, schedulePath);

    await update(scheduleRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });

    dbg('Schedule updated:', scheduleId);
    return { id: scheduleId, ...updates };
  } catch (error) {
    console.error('Error updating schedule:', error);
    throw error;
  }
};

/**
 * Soft-delete a schedule by marking it inactive.
 */
export const deleteSchedule = async (scheduleId, companyId) => {
  try {
    const { database } = getFirebase();

    const schedulePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/schedules/${scheduleId}`
      : `schedules/${scheduleId}`;

    const scheduleRef = ref(database, schedulePath);

    // Soft delete - mark as inactive
    await update(scheduleRef, {
      isActive: false,
      deletedAt: new Date().toISOString()
    });

    dbg('Schedule deleted:', scheduleId);
    return true;
  } catch (error) {
    console.error('Error deleting schedule:', error);
    throw error;
  }
};

/**
 * Replace the full set of employees assigned to a schedule.
 */
export const assignEmployeesToSchedule = async (scheduleId, employeeIds, companyId) => {
  try {
    const { database } = getFirebase();

    const schedulePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/schedules/${scheduleId}`
      : `schedules/${scheduleId}`;

    const scheduleRef = ref(database, schedulePath);

    await update(scheduleRef, {
      assignedEmployees: employeeIds,
      updatedAt: new Date().toISOString()
    });

    dbg('Employees assigned to schedule:', scheduleId);
    return true;
  } catch (error) {
    console.error('Error assigning employees to schedule:', error);
    throw error;
  }
};
